import { healthcareTrainingData, intentPatterns, defaultResponses, medicalDisclaimer } from '../data/healthcareData';
import { extendedHealthcareData } from '../data/extendedHealthcareData';
import { diseaseSymptomMapping, treatmentSuggestions, lifestyleRecommendations } from '../data/advancedHealthcareData';
import { boneData } from '../data/boneData';

export class HealthcareChatbotEngine {
  constructor() {
    this.conversationHistory = [];
    this.userContext = {};
  }

  // Process user input and generate response
  processMessage(userInput) {
    const cleanInput = userInput.toLowerCase().trim();
    
    // Add to conversation history
    this.conversationHistory.push({
      type: 'user',
      message: userInput,
      timestamp: new Date()
    });

    let response = this.generateResponse(cleanInput);
    
    // Add bot response to history
    this.conversationHistory.push({
      type: 'bot',
      message: response,
      timestamp: new Date()
    });

    return response;
  }

  // Main response generation logic
  generateResponse(input) {
    // Check for greetings
    if (this.matchesPattern(input, intentPatterns.greeting)) {
      return this.getGreetingResponse();
    }

    // Check for goodbyes
    if (this.matchesPattern(input, intentPatterns.goodbye)) {
      return this.getGoodbyeResponse();
    }

    // Check for help requests
    if (this.matchesPattern(input, intentPatterns.help)) {
      return this.getHelpResponse();
    }

    // Check for emergency keywords
    if (this.checkForEmergency(input)) {
      return healthcareTrainingData.emergencies.responses[0];
    }

    // Check for medication questions
    if (this.matchesPattern(input, healthcareTrainingData.medications.patterns)) {
      return this.getRandomResponse(healthcareTrainingData.medications.responses);
    }

    // Check for symptoms
    const symptomResponse = this.checkSymptoms(input);
    if (symptomResponse) {
      return symptomResponse;
    }

    // Check for general health topics
    const generalResponse = this.checkGeneralHealth(input);
    if (generalResponse) {
      return generalResponse;
    }

    // Check for preventive care
    if (this.matchesPattern(input, healthcareTrainingData.preventiveCare.patterns)) {
      return this.getRandomResponse(healthcareTrainingData.preventiveCare.responses);
    }

    // Check for medical conditions
    const conditionResponse = this.checkMedicalConditions(input);
    if (conditionResponse) {
      return conditionResponse;
    }

    // Check for body systems information
    const systemResponse = this.checkBodySystems(input);
    if (systemResponse) {
      return systemResponse;
    }

    // Check for nutrition information
    const nutritionResponse = this.checkNutritionInfo(input);
    if (nutritionResponse) {
      return nutritionResponse;
    }

    // Check for mental health topics
    const mentalHealthResponse = this.checkMentalHealth(input);
    if (mentalHealthResponse) {
      return mentalHealthResponse;
    }

    // Check for treatment and management queries
    const treatmentResponse = this.checkTreatmentQueries(input);
    if (treatmentResponse) {
      return treatmentResponse;
    }

    // Check for system-based health queries
    const systemHealthResponse = this.checkSystemBasedHealth(input);
    if (systemHealthResponse) {
      return systemHealthResponse;
    }

    // Check for bone-related queries
    const boneResponse = this.checkBones(input);
    if (boneResponse) {
      return boneResponse;
    }

    // Default response
    return this.getDefaultResponse();
  }

  // Check if input matches any pattern
  matchesPattern(input, patterns) {
    return patterns.some(pattern => input.includes(pattern));
  }

  // Check for emergency situations
  checkForEmergency(input) {
    const emergencyKeywords = [
      'emergency', 'urgent', 'severe pain', 'can\'t breathe', 'chest pain', 
      'heart attack', 'stroke', 'suicide', 'overdose', 'severe bleeding'
    ];
    return emergencyKeywords.some(keyword => input.includes(keyword));
  }

  // Check for symptoms and provide responses
  checkSymptoms(input) {
    for (const [, data] of Object.entries(healthcareTrainingData.symptoms)) {
      if (this.matchesPattern(input, data.patterns)) {
        const response = this.getRandomResponse(data.responses);
        const recommendations = data.recommendations.join(', ');
        return `${response}\n\nRecommendations: ${recommendations}\n\n${medicalDisclaimer}`;
      }
    }
    return null;
  }

  // Check for general health topics
  checkGeneralHealth(input) {
    for (const [, data] of Object.entries(healthcareTrainingData.generalHealth)) {
      if (this.matchesPattern(input, data.patterns)) {
        return this.getRandomResponse(data.responses);
      }
    }
    return null;
  }

  // Get random response from array
  getRandomResponse(responses) {
    return responses[Math.floor(Math.random() * responses.length)];
  }

  // Greeting responses
  getGreetingResponse() {
    const greetings = [
      "Hello! I'm your healthcare assistant. I can help answer general health questions and provide health information. How can I assist you today?",
      "Hi there! I'm here to help with health-related questions and provide general wellness information. What would you like to know?",
      "Good day! I'm your health information assistant. Feel free to ask me about symptoms, general health topics, or wellness tips. How can I help?"
    ];
    return this.getRandomResponse(greetings);
  }

  // Goodbye responses
  getGoodbyeResponse() {
    const goodbyes = [
      "Take care of your health! Remember to consult healthcare professionals for medical concerns. Goodbye!",
      "Stay healthy! Don't hesitate to seek professional medical advice when needed. Have a great day!",
      "Goodbye! Remember that maintaining good health is a journey. Take care of yourself!"
    ];
    return this.getRandomResponse(goodbyes);
  }

  // Help responses
  getHelpResponse() {
    return `I can help you with comprehensive health information:

🩺 **Symptoms & Conditions:**
• Common symptoms: fever, headache, cough, chest pain, back pain, joint pain
• Ask: "I have [symptom]" or "[symptom] treatment"

🏥 **Diseases & Disorders:**
• Heart conditions: heart disease, hypertension, stroke
• Respiratory: asthma, pneumonia, bronchitis
• Metabolic: diabetes, obesity
• Mental health: depression, anxiety, insomnia
• Musculoskeletal: arthritis, osteoporosis, fibromyalgia
• Skin: eczema, psoriasis
• Ask: "What is [disease]?" or "[disease] symptoms"

💊 **Treatment & Prevention:**
• Ask: "How to treat [condition]?" or "Prevention for [disease]?"
• Treatment options, lifestyle changes, prevention strategies

🍎 **Wellness & Lifestyle:**
• Nutrition, exercise, sleep, stress management
• Ask: "Nutrition tips" or "Exercise for [condition]"

🚨 **Emergency Recognition:**
• I can identify serious symptoms requiring immediate medical attention

**Example questions:**
• "I have chest pain and shortness of breath"
• "What is diabetes and its symptoms?"
• "How to manage anxiety?"
• "Prevention tips for heart disease"
• "Arthritis pain relief options"

${medicalDisclaimer}`;
  }

  // Default response when no pattern matches
  getDefaultResponse() {
    return this.getRandomResponse(defaultResponses);
  }

  // Get conversation history
  getConversationHistory() {
    return this.conversationHistory;
  }

  // Clear conversation history
  clearHistory() {
    this.conversationHistory = [];
    this.userContext = {};
  }

  // Advanced: Extract entities from user input (symptoms, body parts, etc.)
  extractEntities(input) {
    const entities = {
      symptoms: [],
      bodyParts: [],
      severity: null,
      duration: null
    };

    // Extract symptoms
    for (const [symptom, data] of Object.entries(healthcareTrainingData.symptoms)) {
      if (this.matchesPattern(input, data.patterns)) {
        entities.symptoms.push(symptom);
      }
    }

    // Extract severity indicators
    const severityPatterns = {
      mild: ['mild', 'slight', 'little', 'minor'],
      moderate: ['moderate', 'medium', 'noticeable'],
      severe: ['severe', 'intense', 'extreme', 'terrible', 'unbearable']
    };

    for (const [level, patterns] of Object.entries(severityPatterns)) {
      if (this.matchesPattern(input, patterns)) {
        entities.severity = level;
        break;
      }
    }

    // Extract duration indicators
    const durationPatterns = [
      'for days', 'for weeks', 'since yesterday', 'all day', 'for hours'
    ];
    
    durationPatterns.forEach(pattern => {
      if (input.includes(pattern)) {
        entities.duration = pattern;
      }
    });

    return entities;
  }

  // Check for medical conditions
  checkMedicalConditions(input) {
    // Check for specific disease names and related terms
    for (const [conditionKey, condition] of Object.entries(extendedHealthcareData.conditions)) {
      const diseasePatterns = [
        conditionKey,
        condition.name.toLowerCase(),
        ...condition.name.toLowerCase().split(' '),
        ...condition.symptoms.map(s => s.toLowerCase())
      ];
      
      // Check if input matches disease name or asks about symptoms
      const isAboutDisease = diseasePatterns.some(pattern => 
        input.includes(pattern) || 
        input.includes(`what is ${pattern}`) ||
        input.includes(`${pattern} symptoms`) ||
        input.includes(`${pattern} treatment`) ||
        input.includes(`${pattern} causes`) ||
        input.includes(`${pattern} prevention`)
      );

      if (isAboutDisease) {
        return `${condition.name}:

${condition.info}

🔸 **Common symptoms:** ${condition.symptoms.join(', ')}
🔸 **Common causes:** ${condition.causes.join(', ')}  
🔸 **Prevention tips:** ${condition.prevention.join(', ')}

${medicalDisclaimer}`;
      }
    }

    // Generic disease inquiry patterns
    const diseasePatterns = [
      'what is', 'tell me about', 'symptoms of', 'causes of', 'treatment for',
      'prevention of', 'how to treat', 'cure for', 'medicine for'
    ];

    if (diseasePatterns.some(pattern => input.includes(pattern))) {
      return `I can provide information about many health conditions including:

🩺 **Common Conditions:** Diabetes, Hypertension, Asthma, Arthritis, Heart Disease, Stroke
🧠 **Mental Health:** Depression, Anxiety, Insomnia, Stress disorders  
🫁 **Respiratory:** Pneumonia, Bronchitis, Allergies
🦴 **Musculoskeletal:** Osteoporosis, Fibromyalgia, Back pain
🔥 **Skin Conditions:** Eczema, Psoriasis
💊 **Digestive:** Gastritis, Kidney stones

Try asking: "What is [disease name]?" or "[Disease] symptoms" or "How to prevent [disease]?"

${medicalDisclaimer}`;
    }
    
    return null;
  }

  // Check for body systems information
  checkBodySystems(input) {
    const systemKeywords = ['heart', 'cardiovascular', 'lungs', 'respiratory', 'digestive', 'stomach'];
    
    for (const keyword of systemKeywords) {
      if (input.includes(keyword)) {
        for (const [systemKey, system] of Object.entries(extendedHealthcareData.bodySystems)) {
          if (input.includes(systemKey) || system.organs.some(organ => input.includes(organ))) {
            return `${system.name}:

Main organs: ${system.organs.join(', ')}
Key functions: ${system.functions.join(', ')}

Health tips for your ${system.name.toLowerCase()}:
• ${system.healthTips.join('\n• ')}

For specific medical concerns about your ${system.name.toLowerCase()}, consult a healthcare provider.`;
          }
        }
      }
    }
    return null;
  }

  // Check for nutrition information
  checkNutritionInfo(input) {
    const nutritionKeywords = ['vitamin', 'protein', 'carb', 'fat', 'nutrition', 'diet', 'nutrient'];
    
    if (nutritionKeywords.some(keyword => input.includes(keyword))) {
      // Check for specific vitamins
      if (input.includes('vitamin c')) {
        const vitC = extendedHealthcareData.nutrition.vitamins.vitamin_c;
        return `Vitamin C:
Function: ${vitC.function}
Good sources: ${vitC.sources.join(', ')}
Deficiency can cause: ${vitC.deficiency.join(', ')}`;
      }
      
      if (input.includes('vitamin d')) {
        const vitD = extendedHealthcareData.nutrition.vitamins.vitamin_d;
        return `Vitamin D:
Function: ${vitD.function}
Good sources: ${vitD.sources.join(', ')}
Deficiency can cause: ${vitD.deficiency.join(', ')}`;
      }

      // Check for macronutrients
      if (input.includes('protein')) {
        const protein = extendedHealthcareData.nutrition.macronutrients.proteins;
        return `Proteins:
Function: ${protein.function}
Good sources: ${protein.sources.join(', ')}
Recommended intake: ${protein.recommended}`;
      }

      // General nutrition advice
      return `Balanced nutrition includes:

🥗 Macronutrients:
• Carbohydrates (45-65% of calories): whole grains, fruits, vegetables
• Proteins (10-35% of calories): lean meats, fish, beans, nuts
• Fats (20-35% of calories): olive oil, avocados, nuts, fish

💊 Key vitamins and minerals from a variety of colorful fruits and vegetables

💧 Adequate hydration with water

For personalized nutrition advice, consult a registered dietitian.`;
    }
    return null;
  }

  // Check for mental health topics
  checkMentalHealth(input) {
    if (input.includes('stress') || input.includes('anxiety') || input.includes('depression') || input.includes('mental health')) {
      if (input.includes('stress')) {
        return `Stress Management Strategies:

• ${extendedHealthcareData.mentalHealth.strategies.stress_management.join('\n• ')}

Remember: If stress becomes overwhelming or persistent, consider seeking professional help from a mental health provider.`;
      }
      
      if (input.includes('anxiety')) {
        return `Anxiety Coping Techniques:

• ${extendedHealthcareData.mentalHealth.strategies.anxiety_coping.join('\n• ')}

Note: If anxiety significantly impacts your daily life, please consider speaking with a mental health professional.`;
      }

      if (input.includes('depression')) {
        return `Depression Support Resources:

• ${extendedHealthcareData.mentalHealth.strategies.depression_support.join('\n• ')}

Important: If you're having thoughts of self-harm, please contact a crisis helpline or emergency services immediately. Depression is treatable with professional help.`;
      }

      return `Mental health is just as important as physical health. Consider:

• Regular self-care practices
• Maintaining social connections  
• Seeking professional help when needed
• Building healthy coping strategies

If you're in crisis, contact:
• National Suicide Prevention Lifeline: 988
• Crisis Text Line: Text HOME to 741741`;
    }
    return null;
  }

  // Check for treatment and management queries
  checkTreatmentQueries(input) {
    const treatmentKeywords = ['treatment', 'treat', 'cure', 'medicine', 'medication', 'therapy', 'management', 'help with'];
    
    if (treatmentKeywords.some(keyword => input.includes(keyword))) {
      // Check for specific condition treatment
      for (const [conditionKey, recommendations] of Object.entries(lifestyleRecommendations)) {
        if (input.includes(conditionKey)) {
          return `Treatment and Management for ${conditionKey.charAt(0).toUpperCase() + conditionKey.slice(1)}:

• ${recommendations.join('\n• ')}

General health recommendations:
• ${treatmentSuggestions.general.slice(0, 4).join('\n• ')}

${medicalDisclaimer}`;
        }
      }

      // Check for pain management
      if (input.includes('pain')) {
        return `Pain Management Strategies:

• ${treatmentSuggestions.pain_management.join('\n• ')}

Remember: Persistent or severe pain should be evaluated by a healthcare professional.

${medicalDisclaimer}`;
      }

      // General treatment advice
      return `General Treatment and Management Principles:

🏥 **Medical Care:**
• Follow your healthcare provider's treatment plan
• Take medications as prescribed
• Attend regular follow-up appointments
• Communicate changes in symptoms

🏠 **Self-Care:**
• ${treatmentSuggestions.general.join('\n• ')}

For specific treatment recommendations, please consult with your healthcare provider.

${medicalDisclaimer}`;
    }
    return null;
  }

  // Check for system-based health queries
  checkSystemBasedHealth(input) {
    for (const [systemKey, systemData] of Object.entries(diseaseSymptomMapping)) {
      if (systemData.patterns.some(pattern => input.includes(pattern))) {
        return `${systemKey.charAt(0).toUpperCase() + systemKey.slice(1).replace('_', ' ')} Health Information:

🔸 **Related Conditions:** ${systemData.diseases.join(', ')}
🔸 **Common Symptoms:** ${systemData.symptoms.join(', ')}

**When to seek medical attention:**
• Symptoms that are severe or worsening
• Symptoms that interfere with daily activities
• New or concerning symptoms
• Symptoms that don't improve with self-care

For specific concerns about your ${systemKey.replace('_', ' ')} health, please consult a healthcare professional.

${medicalDisclaimer}`;
      }
    }
    return null;
  }

  // Check for bone-related queries and respond with bone information
  checkBones(input) {
    const boneKeywords = [
      'bone', 'bones', 'skeleton', 'skeletal', 'skull', 'vertebrae', 'femur', 'humerus', 'clavicle', 'spine', 'rib', 'pelvis',
      'bird bone', 'animal bone', 'amphibian bone', 'human bone', 'how many bones', 'bone structure', 'bone function'
    ];
    if (!boneKeywords.some(keyword => input.includes(keyword))) {
      return null;
    }

    // Determine which group the user is asking about
    if (input.includes('human')) {
      return this.formatBoneInfo('human');
    } else if (input.includes('bird')) {
      return this.formatBoneInfo('bird');
    } else if (input.includes('amphibian')) {
      return this.formatBoneInfo('amphibian');
    } else if (input.includes('animal')) {
      return this.formatBoneInfo('animal');
    } else {
      // If not specified, provide a summary and suggest more
      return (
        'Bones are essential for structure and movement in all vertebrates.\n' +
        '• Humans: ' + boneData.human.description + '\n' +
        '• Animals: ' + boneData.animal.description + '\n' +
        '• Birds: ' + boneData.bird.description + '\n' +
        '• Amphibians: ' + boneData.amphibian.description + '\n' +
        'Ask about a specific group (e.g., "human bones", "bird skeleton") for more details.'
      );
    }
  }

  // Helper to format bone info for a group
  formatBoneInfo(group) {
    const data = boneData[group];
    if (!data) return null;
    let response = `${group.charAt(0).toUpperCase() + group.slice(1)} bones:\n${data.description}`;
    if (data.keyBones) {
      response += '\nKey bones:';
      data.keyBones.forEach(bone => {
        response += `\n• ${bone.name}: ${bone.function}`;
      });
    }
    if (data.commonBones) {
      response += '\nCommon bones:';
      data.commonBones.forEach(bone => {
        response += `\n• ${bone.name}: ${bone.function}`;
      });
    }
    if (data.examples) {
      response += '\nExamples:';
      data.examples.forEach(ex => {
        response += `\n• ${ex.species}: ${ex.bones} bones (${ex.note})`;
      });
    }
    if (data.note) {
      response += `\nNote: ${data.note}`;
    }
    return response;
  }
}
